import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import { useEffect, useState } from "react";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import doc_processing_icon from "../../../images/doc_processing_icon.png";
import Header from "../../Includes/Header";
import verify_success_icon from "../../../images/verify_success_icon.png";
import config from "../../../config";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Spacer from "MyComponents/UI/Spacer";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { submitPersonalKycDraft } from "Utils/personalKycDraft";
import { useTranslation } from "react-i18next";

interface GlobalPEPResponse {
  data: any;
}

interface DojahAMLResponse {
  ProfileID: string;
}

interface SEONScreeningResponse {
  data: any;
}

const DocumentProcessing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clientReference = useSelector((state: RootState) => state.auth.userID);
  const dob = useSelector((state: RootState) => state.auth.dateofBirth);
  const email = useSelector((state: RootState) => state.auth.email);
  const phoneNumber = useSelector((state: RootState) => state.auth.phoneNo);
  const firstNameState = useSelector((state: RootState) => state.auth.firstName);
  const lastNameState = useSelector((state: RootState) => state.auth.lastName);
  const datazoo_globalPEPAndSanctionScreening_baseURL =
    config.NODE_API_BASE_URL + "/dataZoo/globalPEPAndSanctionScreening";
  const dojah_userAMLScreening_baseURL =
    config.NODE_API_BASE_URL + "/dojah/userAMLScreening";
  const dojah_userScreeningInfo_baseURL =
    config.NODE_API_BASE_URL + "/dojah/userScreeningInfo";
  const seon_fraudCheck_baseURL =
    config.NODE_API_BASE_URL_V2 + "/seon/fraudCheck";
  const { userId } = useParams<{ userId: string }>();

  const [tick1, setTick1] = useState(false);
  const [tick2, setTick2] = useState(false);
  const [tick3, setTick3] = useState(false);
  const [tick4, setTick4] = useState(false);
  const [tick5, setTick5] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const globalPEPAndSanctionScreening = async (
    clientReference: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string
  ): Promise<GlobalPEPResponse | undefined> => {
    try {
      const response = await axios.post(
        datazoo_globalPEPAndSanctionScreening_baseURL,
        { clientReference, firstName, lastName, dateOfBirth }
      );
      if (response.status === 200) {
        setTick1(true);
        return response.data;
      }
    } catch (error) {
      console.error("Error in Global PEP Sanction Screening:", error);
    }
  };

  const userAMLScreening = async (
    firstName: string,
    lastName: string,
    dateOfBirth: string
  ): Promise<DojahAMLResponse | undefined> => {
    try {
      const response = await axios.post(dojah_userAMLScreening_baseURL, {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
      });
      if (response.status === 200) {
        setTick2(true);
        return response.data;
      }
    } catch (error) {
      console.error("Error in User AML Screening:", error);
    }
  };

  const userAMLScreeningInfo = async (profileId: string): Promise<any | undefined> => {
    try {
      const response = await axios.post(dojah_userScreeningInfo_baseURL, { profile_id: profileId });
      if (response.status === 200) {
        setTick3(true);
        setTick4(true);
        return response.data;
      }
    } catch (error) {
      console.error("Error in User AML Screening Info:", error);
    }
  };

  const sampleResponse = {
    success: true,
    error: {},
    data: {
      id: "67c2810c2de1",
      state: "DECLINE",
      fraud_score: 95.75,
      blackbox_score: 93.25,
      bin_details: {
        card_bin: "414141",
        bin_bank: "VERMONT NATIONAL BANK",
        bin_card: "VISA",
        bin_type: "CREDIT",
        bin_level: "CLASSIC",
        bin_country: "UNITED STATES",
        bin_country_code: "US",
        bin_website: "www.vermontnationalbank.com",
        bin_phone: "+1 802 476 0030",
        bin_valid: true,
        card_issuer: "VISA",
      },
      version: "v2",
      transaction_details: {
        amount: 0,
        currency: "USD",
        ipAddress: "198.51.100.42",
        deviceId: "device-id-123",
        location: { latitude: 47.4810949, longitude: 18.9651553 },
        paymentMethod: "credit_card",
        cardDetails: {
          cardNumber: "4111111111111111",
          cardType: "VISA",
          expiryDate: "2025-03-19",
        },
        metadata: { additionalProp1: {} },
      },
    },
  };

  const seonFraudCheck = async (
    userId: string,
    email: string,
    phoneNumber: string
  ): Promise<SEONScreeningResponse | undefined> => {
    const transactionDetails = {
      amount: sampleResponse.data.transaction_details.amount,
      currency: sampleResponse.data.transaction_details.currency,
      ipAddress: sampleResponse.data.transaction_details.ipAddress,
      deviceId: sampleResponse.data.transaction_details.deviceId,
      location: {
        latitude: sampleResponse.data.transaction_details.location.latitude,
        longitude: sampleResponse.data.transaction_details.location.longitude,
      },
      paymentMethod: sampleResponse.data.transaction_details.paymentMethod,
      cardDetails: {
        cardNumber: sampleResponse.data.transaction_details.cardDetails.cardNumber,
        cardType: sampleResponse.data.transaction_details.cardDetails.cardType,
        expiryDate: sampleResponse.data.transaction_details.cardDetails.expiryDate,
      },
      metadata: sampleResponse.data.transaction_details.metadata,
    };

    setTick5(true);
    try {
      const response = await axios.post(seon_fraudCheck_baseURL, {
        userId, email, phoneNumber, transactionDetails,
      });
      if (response.status === 200) {
        setTick5(true);
        return response.data;
      }
    } catch (error) {
      console.error("Error in SEON Fraud Check:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pepResponse = await globalPEPAndSanctionScreening(clientReference, firstNameState, lastNameState, dob);
        const dojahResponse = await userAMLScreening(firstNameState, lastNameState, dob);
        if (dojahResponse) {
          const profileID = dojahResponse.ProfileID;
          const screeningInfo = await userAMLScreeningInfo(profileID);
        }

        const seonResponse = await seonFraudCheck(userId!, email, phoneNumber);

        if (clientReference) {
          setIsSubmitting(true);
          const submitResponse = await submitPersonalKycDraft(clientReference.toString());
          if (submitResponse && submitResponse.code === 200) {
            setIsSubmitted(true);
          } else {
            console.error("Final draft submission failed:", submitResponse);
          }
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Container fluid className="px-0 px-md-3">
        <Row className="g-0">
          <Col md={12} className="mx-auto px-2 pt-0 pb-3">
            <div className="mt-5">
              <Row className="px-2 px-md-5 mt-5 text-center">
                <Col md={12}>
            {tick1 && tick2 && tick3 && tick4 && tick5 && isSubmitted ? (
              <>
                <img src={verify_success_icon} width={60} alt="Success" />
                <h3 className="mt-3">{t('kyc.thank_you')}</h3>
              </>
            ) : (
              <>
                <img src={doc_processing_icon} width={60} alt="Processing" className="fa-spin" />
                <h3 className="mt-3">{t('kyc.processing_data')}</h3>
              </>
            )}
            <p className="text-secondary mt-3">{t('kyc.processing_desc')}</p>
          </Col>
        </Row>
        <Row className="px-2 px-md-5">
          <Col md={12}>
            <table className="mx-auto tbl-row-space-3">
              <tr>
                <td>
                  {tick1 ? (
                    <i className="fa fa-solid fa-circle-check text-primary"></i>
                  ) : (
                    <Spinner animation="border" variant="primary" size="sm" />
                  )}
                </td>
                <td>
                  <label className="form-label px-2 fw-bold mb-0">{t('kyc.doc_info_label')}</label>
                </td>
              </tr>
              <tr>
                <td>
                  {tick2 ? (
                    <i className="fa fa-solid fa-circle-check text-primary"></i>
                  ) : (
                    <Spinner animation="border" variant="primary" size="sm" />
                  )}
                </td>
                <td>
                  <label className="form-label px-2 fw-bold mb-0">{t('kyc.identity_info_label')}</label>
                </td>
              </tr>
              <tr>
                <td>
                  {tick3 ? (
                    <i className="fa fa-solid fa-circle-check text-primary"></i>
                  ) : (
                    <Spinner animation="border" variant="primary" size="sm" />
                  )}
                </td>
                <td>
                  <label className="form-label px-2 fw-bold mb-0">{t('kyc.address_info_label')}</label>
                </td>
              </tr>
              <tr>
                <td>
                  {tick4 ? (
                    <i className="fa fa-solid fa-circle-check text-primary"></i>
                  ) : (
                    <Spinner animation="border" variant="primary" size="sm" />
                  )}
                </td>
                <td>
                  <label className="form-label px-2 fw-bold mb-0">{t('kyc.fraud_check_label')}</label>
                </td>
              </tr>
              <tr>
                <td>
                  {tick5 ? (
                    <i className="fa fa-solid fa-circle-check text-primary"></i>
                  ) : (
                    <Spinner animation="border" variant="primary" size="sm" />
                  )}
                </td>
                <td>
                  <label className="form-label px-2 fw-bold mb-0">{t('kyc.questionnaire')}</label>
                </td>
              </tr>
            </table>
          </Col>
          <Col md={12} className="text-center mt-4">
            {tick1 && tick2 && tick3 && tick4 && tick5 && isSubmitted ? (
              <button
                className="mt-3 btn btn-primary rounded-5"
                onClick={() => {
                  localStorage.removeItem('pageName');
                  navigate("/personal_account_created");
                }}
              >
                {t('common.continue')}
              </button>
            ) : (
              <></>
            )}
          </Col>
        </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DocumentProcessing;
